import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'react-toastify';

import { externalMatchesService } from 'app/providers/api/services/externalMatches.service';
import { SupplierService } from 'app/providers/api/services/supplier.service';

export const useExternalMatchesPage = () => {
  const queryClient = useQueryClient();
  const [uploadJobId, setUploadJobId] = useState<string | null>(null);

  // Fetch external match statistics
  const {
    data: stats,
    isLoading: isStatsLoading,
    error: statsError,
  } = useQuery({
    queryKey: ['external-matches-stats'],
    queryFn: externalMatchesService.getStats,
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Fetch suppliers
  const { data: suppliers, isLoading: isSuppliersLoading } = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => SupplierService.getAllSuppliers(),
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async ({
      file,
      request,
    }: {
      file: File;
      request: {
        name: string;
        sourceName: string;
        sourceType: 'internal' | 'supplier';
        sourceSupplierId?: string;
        flushExisting?: boolean;
      };
    }) => {
      return externalMatchesService.uploadMatches(file, request);
    },
    onSuccess: (data) => {
      setUploadJobId(data.jobId);
      toast.success('Upload started successfully');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to upload file');
    },
  });

  // Upload status query (only when we have a job ID)
  const { data: uploadStatus, isLoading: isUploadStatusLoading } = useQuery({
    queryKey: ['external-matches-upload-status', uploadJobId],
    queryFn: () => externalMatchesService.getUploadStatus(uploadJobId!),
    enabled: !!uploadJobId,
    refetchInterval: (data) => {
      // Stop polling when upload is completed or failed
      if (data?.status === 'completed' || data?.status === 'failed') {
        return false;
      }
      return 2000; // Poll every 2 seconds while processing
    },
  });

  const handleUpload = async (
    file: File,
    sourceName: string,
    sourceType: 'internal' | 'supplier',
    sourceSupplierId?: string,
    flushExisting?: boolean,
  ) => {
    try {
      await uploadMutation.mutateAsync({
        file,
        request: {
          name: `${sourceType}_${sourceName}_${
            new Date().toISOString().split('T')[0]
          }`,
          sourceName,
          sourceType,
          sourceSupplierId,
          flushExisting,
        },
      });
    } catch (error) {
      // Error is handled by the mutation's onError
    }
  };

  const clearUploadStatus = () => {
    setUploadJobId(null);
    queryClient.removeQueries({
      queryKey: ['external-matches-upload-status'],
    });
  };

  return {
    // Stats
    stats,
    isStatsLoading,
    statsError,

    // Suppliers
    suppliers,
    isSuppliersLoading,

    // Upload
    handleUpload,
    isUploading: uploadMutation.isLoading,
    uploadError: uploadMutation.error,

    // Upload Status
    uploadJobId,
    uploadStatus,
    isUploadStatusLoading,
    clearUploadStatus,

    // Refresh stats
    refetchStats: () => {
      queryClient.invalidateQueries({
        queryKey: ['external-matches-stats'],
      });
    },
  };
};
